import * as React from "react";
import { Popover, ArrowContainer, PopoverPosition, PopoverAlign } from "react-tiny-popover";
import colors from "../styles/colors";

const CustomPopover: React.FC<React.PropsWithChildren<ICustomPopoverProps>> = (props) => {
    const [isPopoverOpen, setIsPopoverOpen] = React.useState<boolean>(false);

    return (
        <CustomPopoverBase
            {...props}
            isOpen={isPopoverOpen}
            onClickOutside={() => setIsPopoverOpen(false)}
            onTooltipClick={() => setIsPopoverOpen(prev => !prev)}
            onTooltipMouseLeave={props.useHover ? () => setIsPopoverOpen(false) : undefined}
            onChildrenClick={!props.useHover ? () => setIsPopoverOpen(prev => !prev) : undefined}
            onChildrenMouseEnter={props.useHover ? () => setIsPopoverOpen(prev => !prev) : undefined}
            onChildrenMouseLeave={props.useHover ? () => setIsPopoverOpen(prev => !prev) : undefined}
        />
    );
}

export const CustomPopoverBase: React.FC<React.PropsWithChildren<ICustomPopoverBaseProps>> = (props) => {
    const [align, setAlign] = React.useState<PopoverAlign | undefined>(undefined);
    const clickMeButtonRef = React.useRef<HTMLButtonElement | null>(null);

    React.useEffect(() => {
        if (props.isOpen && props.onOpenHandler) {
            props.onOpenHandler();
        }
    }, [props.isOpen]);

    const CONTAINER_MAX_WIDTH = 775;

    React.useEffect(() => {
        if (!props.boundaryElementRef?.current) {
            return;
        }

        const boundaryRect = props.boundaryElementRef.current.getBoundingClientRect();
        const sideMaxLength = CONTAINER_MAX_WIDTH / 2;

        if (boundaryRect.left - sideMaxLength < 0) {
            setAlign('start');
        }
        if (window.innerWidth < boundaryRect.right + sideMaxLength) {
            setAlign('end');
        }
    }, [props]);

    const position = props.position ?? ['top', 'right', 'left', 'bottom'];
    const arrowColor = props.arrowColor ?? colors.grey_1;
    const toolTipContainerStyles: React.CSSProperties = {
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        backgroundColor: colors.grey_1,
        padding: '10px',
        whiteSpace: 'break-spaces',
        ...props.popoverContentStyles
    };

    return (
        <Popover
            isOpen={props.isOpen}
            positions={position}
            align={align}
            boundaryElement={props.boundaryElementRef?.current || undefined}
            padding={10}
            onClickOutside={props.onClickOutside}
            ref={clickMeButtonRef}
            containerStyle={{ color: colors.white, zIndex: "900000" }}
            content={({ position, childRect, popoverRect }) => (
                <ArrowContainer
                    position={position}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor={arrowColor}
                    arrowSize={5}
                    className='popover-arrow-container'
                    arrowClassName='popover-arrow'
                >
                    <div
                        style={toolTipContainerStyles}
                        onClick={props.onTooltipClick}
                        onMouseLeave={props.onTooltipMouseLeave}
                    >
                        {props.tooltip}
                    </div>
                </ArrowContainer>
            )}
        >
            <div
                style={props.clickHandlerStyles}
                onClick={props.onChildrenClick}
                onMouseEnter={props.onChildrenMouseEnter}
                onMouseLeave={props.onChildrenMouseLeave}
            >
                {props.children}
            </div>
        </Popover>
    );
}

export interface ICustomPopoverProps {
    tooltip: JSX.Element | string;
    clickHandlerStyles?: React.CSSProperties;
    useHover?: boolean;
    position?: PopoverPosition[];
    popoverContentStyles?: React.CSSProperties;
    arrowColor?: string;
    onOpenHandler?: () => void;
    boundaryElementRef?: React.MutableRefObject<HTMLElement | null>;
}

interface ICustomPopoverBaseProps extends ICustomPopoverProps {
    isOpen: boolean;
    onClickOutside?: () => void;
    onTooltipClick?: () => void;
    onTooltipMouseLeave?: () => void;
    onChildrenClick?: () => void;
    onChildrenMouseEnter?: () => void;
    onChildrenMouseLeave?: () => void;
}

export default CustomPopover;